import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import MobileMenu2 from "./modules/shared/components/MobileMenu";
import SharedFooter3 from "./Footer/SharedFooter3";
import { getTranslatedCategoryName } from "./utils/categoryTranslation";
import {
  Search,
  Globe,
  X,
  MessageSquare,
  Mail,
  HelpCircle,
  AlertCircle,
  Users,
  Star,
  Shield,
  Heart,
  Info,
  ChevronDown,
  Languages,
  Filter,
  MapPin,
  MessageCircle,
  Check,
  Calendar,
  Clock,
  Zap,
  Package,
  Phone,
  User,
  FileText,
  ThumbsUp,
  Send,
  Home
} from "lucide-react";
import PolicyModals from "./pages/Main/Policy";
import { useSelector } from "react-redux";
import { useAppDispatch, LogoutUser, LogoutManually, getTypeCategories, getLocalProfessionals } from "./redux";
import { TypeEnum } from "./modules/shared/model/type.enum";
import MyProfile from "./modules/professional/components/MyProfile";
import { RequestQuoteModal } from "./components/ui";
import ReviewModal from "./components/ui/ReviewModal";
import { RoleEnum } from "./modules";
import { useNavigate } from "react-router-dom";
import LanguageSelector from "./components/LanguageSelector";

// Mock user data
const mockUserData = {
  isLoggedIn: false // Assuming user is not logged in by default
};

// Random icons array for categories
const randomIcons = [
  AlertCircle, Shield, FileText, Heart, Users, Star,
  HelpCircle, Clock, Phone, Mail, MapPin, Globe,
  Check, Zap, Package, Home, User, MessageCircle
];

// Popular search suggestions
const popularSearches = [
  "plumbing", "electrical", "cleaning", "legal",
  "renovation", "gardening", "carpentry", "painting"
];


const InfoPopup = ({ isOpen, onClose, title, content }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl max-w-lg w-full p-6 relative">
        <button onClick={onClose} className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full"><X className="h-5 w-5" /></button>
        <h3 className="text-xl font-semibold mb-4">{title}</h3>
        <div className="prose max-w-none">{content}</div>
      </motion.div>
    </motion.div>
  );
};

const WelcomePopup = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  useEffect(() => {
    const hasSeenPopup = localStorage.getItem("hasSeenWelcomePopup");
    if (!hasSeenPopup) {
      setTimeout(() => { onClose(); localStorage.setItem("hasSeenWelcomePopup", "true"); }, 4000);
    } else {
      onClose();
    }
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="fixed bottom-[15%] left-4 right-4 sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md bg-white p-4 sm:p-6 rounded-xl shadow-lg z-50">
      <h3 className="text-xl font-semibold mb-2">{t("homepage.welcomePopup.title")}</h3>
      <p className="text-gray-600">{t("homepage.welcomePopup.subtitle")}</p>
    </motion.div>
  );
};

const LoginRegistrationPopup = ({ isOpen, onClose, handleContinueWithEmail, handleLogin }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-2 sm:p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-[20px] max-w-[90%] sm:max-w-md w-full shadow-lg"
      >
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="flex items-center">
              <img src="https://assests.netlify.app/assets/images/logo.png" alt="Kokobeo" className="h-6 sm:h-8" />
              <span className="ml-2 text-blue-600 text-lg sm:text-xl font-bold">Kokobeo</span>
            </div>
            <button
              onClick={onClose}
              className="p-1 sm:p-2 hover:bg-gray-100 rounded-full bg-red-500 text-white"
            >
              <X className="h-3 sm:h-5 w-3 sm:w-5" />
            </button>
          </div>
          <h3 className="text-base sm:text-xl font-semibold mb-2 sm:mb-4">{t("homepage.loginPopup.createAccount")}</h3>
          <p className="text-gray-600 text-sm sm:text-base mb-2 sm:mb-4">{t("homepage.loginPopup.iWantTo")}</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4 sm:mb-6">
            <button
              onClick={() => { }}
              className="w-full sm:flex-1 bg-white border border-gray-300 rounded-lg p-2 sm:p-4 flex flex-col items-center hover:bg-gray-50"
            >
              <User className="h-4 sm:h-6 w-4 sm:w-6 text-gray-500 mb-1 sm:mb-2" />
              <span className="text-xs sm:text-sm text-gray-700">{t("homepage.loginPopup.hireProfessional")}</span>
            </button>
            <button
              onClick={() => { }}
              className="w-full sm:flex-1 bg-white border border-gray-300 rounded-lg p-2 sm:p-4 flex flex-col items-center hover:bg-gray-50"
            >
              <User className="h-4 sm:h-6 w-4 sm:w-6 text-gray-500 mb-1 sm:mb-2" />
              <span className="text-xs sm:text-sm text-gray-700">{t("homepage.loginPopup.workAsProfessional")}</span>
            </button>
          </div>
          <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-4">
            <button
              className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50"
            >
              <img src="https://cdn1.iconfinder.com/data/icons/google-s-logo/150/Google_Icons-09-512.png" alt="Google" className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2" />Google
            </button>
            <button
              className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50"
            >
              <img src="https://www.shutterstock.com/image-vector/batumi-georgia-december-1-2023-600nw-2395541231.jpg" alt="Apple" className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2" />Apple
            </button>
            <button
              className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50"
            >
              <img src="https://cdn2.iconfinder.com/data/icons/social-media-2210/24/Facebook-512.png" alt="Facebook" className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2" />Facebook
            </button>
            <button
              className="w-[48%] sm:flex-1 bg-white border border-gray-300 rounded-lg p-1 sm:p-2 flex items-center justify-center hover:bg-gray-50"
            >
              <img src="https://cdn3.iconfinder.com/data/icons/social-media-2210/24/Instagram-512.png" alt="Instagram" className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2" />Instagram
            </button>
          </div>
          <p className="text-gray-500 text-xs sm:text-sm mb-2 sm:mb-4">{t("homepage.loginPopup.or")}</p>
          <button
            onClick={handleContinueWithEmail}
            className="w-full bg-blue-600 text-white py-1.5 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm"
          >
            {t("homepage.loginPopup.continueWithEmail")}
          </button>
          <p className="text-sm text-gray-600 mt-2 text-center text-xs sm:text-sm">
            {t("homepage.loginPopup.alreadyHaveAccount")}
            <button
              onClick={handleLogin}
              className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm"
            >
              {t("homepage.loginPopup.logIn")}
            </button>
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ChatModal = ({ isOpen, onClose, service, onLogin }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState(t("homepage.chatModal.defaultMessage"));
  if (!isOpen) return null;
  const handleSend = () => {
    onLogin();
  };
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center p-2 sm:p-4">
        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl w-full max-w-2xl h-[85vh] sm:h-[600px] flex flex-col shadow-2xl">
          <div className="flex-shrink-0 p-3 sm:p-4 border-b flex justify-between items-center bg-gradient-to-r from-blue-50 to-white">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate pr-4">{t("homepage.chatModal.title", { service })}</h3>
            <button onClick={onClose} className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 p-3 sm:p-4 overflow-y-auto bg-gray-50">
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-white rounded-lg p-3 sm:p-4 max-w-[85%] shadow-sm border border-gray-200">
                <p className="text-sm sm:text-base text-gray-700">{message}</p>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 p-3 sm:p-4 border-t bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={t("homepage.chatModal.placeholder")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 sm:px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
              <button
                onClick={handleSend}
                className="bg-blue-600 text-white p-2 sm:p-2.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex-shrink-0"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const ServiceCard = ({ professional, onMessage, onContact, onViewProfile }) => {
  const { t } = useTranslation();
  const [showReviewModal, setShowReviewModal] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl p-4 sm:p-5 md:p-6 hover:shadow-lg transition-all bg-white">
      <div className="flex items-start gap-3 sm:gap-4 mb-4">
        {professional.avatar ? (
          <img src={professional.avatar} alt={professional.name} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{professional.name}</h3>
              <p className="text-blue-600 text-sm sm:text-base truncate">{professional.service}</p>
              <button
                onClick={() => onViewProfile(professional)}
                className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-medium hover:underline mt-1"
              >
                {t("homepage.serviceCard.viewProfile")}
              </button>
            </div>
            <div className="flex sm:flex-col items-start sm:items-end gap-2">
              <div className="flex items-center">
                <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-current" />
                <span className="ml-1 font-medium text-sm sm:text-base">{professional.rating}</span>
                <span className="text-gray-500 text-xs sm:text-sm ml-1">({professional.reviewsCount})</span>
              </div>
              <button 
                onClick={() => setShowReviewModal(true)} 
                className="px-2.5 sm:px-3 py-1 bg-blue-100 text-blue-600 rounded-md text-xs sm:text-sm hover:bg-blue-200 transition-colors whitespace-nowrap"
              >
                {t("homepage.serviceCard.seeReviews")}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
        {professional.backgroundChecked && (
          <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Shield className="w-3 h-3 mr-1" />
            <span className="hidden sm:inline">{t("homepage.serviceCard.backgroundChecked")}</span>
            <span className="sm:hidden">{t("homepage.serviceCard.verified")}</span>
          </span>
        )}
        {professional.insuranceVerified && (
          <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Check className="w-3 h-3 mr-1" />
            <span className="hidden sm:inline">{t("homepage.serviceCard.insuranceVerified")}</span>
            <span className="sm:hidden">{t("homepage.serviceCard.insured")}</span>
          </span>
        )}
      </div>
      
      {/* Details */}
      <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
        <div className="flex items-start text-sm sm:text-base text-gray-600">
          <span className="text-gray-500 font-medium min-w-[70px] sm:min-w-[80px]">{t("homepage.serviceCard.location")}:</span>
          <div className="ml-2 flex flex-wrap gap-1 flex-1">
            {professional.location && typeof professional.location === "string" ? (
              <span className="text-gray-700 text-sm">{professional.location}</span>
            ) : professional.location && Array.isArray(professional.location) && professional.location.length > 0 ? (
              professional.location.map((loc, index) => (
                <span key={index} className="inline-flex items-center px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {loc.cities && loc.cities.length >0 && loc.cities.join(", ") || t("homepage.serviceCard.unknown")}, {loc.province || t("homepage.serviceCard.unknown")}
                </span>
              ))
            ) : (
              <span className="text-gray-500 text-sm">{t("homepage.serviceCard.locationNotSpecified")}</span>
            )}
          </div>
        </div>
        <p className="text-gray-600 text-sm sm:text-base line-clamp-2">{professional.description}</p>
        {professional.specializations && professional.specializations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {professional.specializations.slice(0, 3).map((spec, index) => (
              <span key={index} className="px-2 py-0.5 sm:py-1 bg-gray-100 rounded-full text-xs text-gray-600">{spec}</span>
            ))}
            {professional.specializations.length > 3 && (
              <span className="px-2 py-0.5 sm:py-1 bg-gray-100 rounded-full text-xs text-gray-500">+{professional.specializations.length - 3}</span>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
          <div>
            <span className="text-gray-500 font-medium">{t("homepage.serviceCard.price")}:</span>
            <span className="ml-1.5 sm:ml-2 font-semibold text-gray-900">{professional.price}</span>
          </div>
          <div>
            <span className="text-gray-500 font-medium">{t("homepage.serviceCard.available")}:</span>
            <span className="ml-1.5 sm:ml-2 font-semibold text-gray-900">{professional.availability}</span>
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-2 sm:gap-3 mt-4">
        <button 
          onClick={() => onContact(professional)} 
          className="flex-1 bg-blue-600 text-white py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base shadow-sm"
        >
          {t("homepage.serviceCard.contactNow")}
        </button>
        <button 
          onClick={() => onMessage(professional)} 
          className="flex items-center justify-center p-2 sm:p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <MessageCircle className="h-5 w-5 text-gray-600" />
        </button>
      </div>
      <AnimatePresence>
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          professional={professional.origionalProfessional}
        />
      </AnimatePresence>
    </div>
  );
};

const ServicePage = ({ service, onClose, onLogin, selectedCategoryId }) => {
  const { t } = useTranslation();
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [priceRange, setPriceRange] = useState([0, 150]); // [min, max]
  const [showFilters, setShowFilters] = useState(false);
  const [showChat, setShowChat] = useState(null);
  const [showEmergencyOnly, setShowEmergencyOnly] = useState(false);
  const [limit] = useState(8);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const localProfessionalsState = useSelector((state: any) => state.admin.localProfessionals);
  const {
    data: rawProfessionals = [],
    totalCount = 0,
    hasNextPage = false,
    currentPage: storeCurrentPage = 1,
    totalPages = 1,
    locationFilters = EMPTY_LOCATION_FILTERS
  } = localProfessionalsState || {};
  const isLoading = useSelector((state: any) => state.loader.isLoading);
  const { user } = useSelector((state: any) => state.auth);
  const currentPage = storeCurrentPage;
  const [minPrice, maxPrice] = priceRange;
  const { provinces = [], citiesByProvince = {}, municipalities = [] } = locationFilters;
  const hasFetchedInitialRef = useRef(false);

  const provinceOptions = useMemo(() => provinces, [provinces]);
  const municipalityOptions = useMemo(() => {
    if (selectedProvince && Array.isArray(citiesByProvince[selectedProvince])) {
      return citiesByProvince[selectedProvince];
    }
    return municipalities;
  }, [selectedProvince, citiesByProvince, municipalities]);

  useEffect(() => {
    if (selectedProvince && !provinceOptions.includes(selectedProvince)) {
      setSelectedProvince("");
    }
  }, [selectedProvince, provinceOptions]);

  useEffect(() => {
    if (selectedMunicipality && !municipalityOptions.includes(selectedMunicipality)) {
      setSelectedMunicipality("");
    }
  }, [selectedMunicipality, municipalityOptions]);

  const handleFilterChange = useCallback(
    (page = 1) => {
      dispatch(
        getLocalProfessionals({
          page,
          limit,
          categoryId: selectedCategoryId,
          minHourlyRate: minPrice > 0 ? minPrice : undefined,
          maxHourlyRate: maxPrice > 0 ? maxPrice : undefined,
          isEmergency: showEmergencyOnly ? true : undefined,
          province: selectedProvince || undefined,
          municipality: selectedMunicipality || undefined,
        })
      );
    },
    [dispatch, limit, selectedCategoryId, minPrice, maxPrice, showEmergencyOnly, selectedProvince, selectedMunicipality]
  );

  const handleResetFilters = useCallback(() => {
    setSelectedProvince("");
    setSelectedMunicipality("");
    setPriceRange([0, 150]);
    setShowEmergencyOnly(false);
    handleFilterChange(1);
  }, [handleFilterChange]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages || page === currentPage) {
        return;
      }
      handleFilterChange(page);
    },
    [currentPage, totalPages, handleFilterChange]
  );

  useEffect(() => {
    if (selectedCategoryId) {
      handleFilterChange(1);
    }
  }, [selectedCategoryId, handleFilterChange]);

  const professionals = useMemo(() => {
    return rawProfessionals.map((professional, index) => {
      const categories = professional.categories && professional.categories.length > 0
        ? professional.categories.map(cat => cat.name).filter(Boolean)
        : [];

      const skills = professional.skills && professional.skills.length > 0
        ? professional.skills.map(skill => skill.name || skill).filter(Boolean)
        : [];

      return {
        id: professional.id || index + 1,
        name: professional.name,
        avatar: professional.photo?.path,
        service: professional.title,
        rating: professional.averageRating || 0,
        reviews: professional.reviewsList || [],
        reviewsCount: professional.reviewsCount || 0,
        location: professional.location || [],
        description: professional.bio || professional.description || "Professional service provider",
        price: professional.hourlyRate ? `${professional.hourlyRate}/hour` : "$0/hour",
        outingCost: "$30",
        availability: "24/7",
        certifications: categories,
        completedJobs: professional.completedJobs || 450,
        responseTime: professional.responseTime || "Under 30 mins",
        insuranceVerified: professional.insured || false,
        backgroundChecked: professional.verified || false,
        specializations: skills,
        nextAvailable: "Today",
        origionalProfessional: professional
      };
    });
  }, [rawProfessionals]);

  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteProfessional, setQuoteProfessional] = useState<any>(null);

  const handleOpenQuoteFlow = useCallback((professional: any) => {
    if (!user?.id) {
      setShowChat(professional.service);
      return;
    }
    if (Number(user?.role?.id) === RoleEnum.client) {
      setQuoteProfessional({ id: professional.id, name: professional.name });
      setShowQuoteModal(true);
      return;
    }
    setShowChat(professional.service);
  }, [user]);

  const handleMessage = useCallback((professional: any) => {
    handleOpenQuoteFlow(professional);
  }, [handleOpenQuoteFlow]);

  const handleContact = useCallback((professional: any) => {
    handleOpenQuoteFlow(professional);
  }, [handleOpenQuoteFlow]);

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-0 sm:p-4 md:p-6"
    >
      <div className="w-full h-full sm:h-auto sm:max-h-[95vh] sm:max-w-6xl sm:rounded-xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-4 sm:p-6 border-b flex justify-between items-center bg-gradient-to-r from-blue-600 to-blue-800 text-white sm:rounded-t-xl">
          <div className="flex-1 min-w-0 pr-4">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold truncate">{t("homepage.servicePage.title", { service })}</h2>
            <p className="text-blue-100 text-xs sm:text-sm mt-1 truncate">{t("homepage.servicePage.subtitle")}</p>
          </div>
          <button 
            onClick={onClose} 
            className="flex-shrink-0 p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Scrollable Content Container */}
        <div className="flex-1 overflow-y-auto">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden p-3 sm:p-4 border-b bg-gray-50 sticky top-0 z-10">
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 py-2.5 px-4 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Filter className="h-4 w-4" />
              <span className="font-medium">{showFilters ? t("homepage.servicePage.hideFilters") : t("homepage.servicePage.showFilters")}</span>
            </button>
          </div>

          {/* Filters Section */}
          <div className={`p-4 sm:p-5 md:p-6 border-b bg-gray-50 ${showFilters ? "block" : "hidden lg:block"}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-3 sm:space-y-4">
                <SearchableDropdown
                  label={t("homepage.servicePage.filters.province")}
                  value={selectedProvince}
                  onChange={setSelectedProvince}
                  options={provinceOptions}
                  placeholder={t("homepage.servicePage.filters.allProvinces")}
                />
                <SearchableDropdown
                  label={t("homepage.servicePage.filters.municipality")}
                  value={selectedMunicipality}
                  onChange={setSelectedMunicipality}
                  options={municipalityOptions}
                  placeholder={t("homepage.servicePage.filters.allMunicipalities")}
                  disabled={municipalityOptions.length === 0}
                />
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div className="text-left">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("homepage.servicePage.filters.priceRange")}</label>
                  <div className="space-y-2">
                    <input
                      type="number"
                      placeholder={t("homepage.servicePage.filters.minPrice")}
                      value={minPrice || ""}
                      onChange={(e) => setPriceRange([Number(e.target.value), maxPrice])}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 text-sm sm:text-base"
                    />
                    <input
                      type="number"
                      placeholder={t("homepage.servicePage.filters.maxPrice")}
                      value={maxPrice || ""}
                      onChange={(e) => setPriceRange([minPrice, Number(e.target.value)])}
                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 text-sm sm:text-base"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div className="text-left">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("homepage.servicePage.filters.emergencyOnly")}</label>
                  <button
                    onClick={() => setShowEmergencyOnly(!showEmergencyOnly)}
                    className={`w-full py-2 px-4 rounded-lg border text-sm sm:text-base font-medium transition-colors ${showEmergencyOnly ? "bg-red-50 border-red-200 text-red-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                  >
                    {showEmergencyOnly ? t("homepage.servicePage.filters.emergencyOnlyActive") : t("homepage.servicePage.filters.allServices")}
                  </button>
                </div>
                <button
                  onClick={() => handleFilterChange(1)}
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm sm:text-base shadow-sm"
                >
                  {isLoading ? t("homepage.servicePage.filters.applying") : t("homepage.servicePage.filters.applyFilters")}
                </button>
                <button
                  onClick={handleResetFilters}
                  disabled={isLoading}
                  className="w-full border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm sm:text-base"
                >
                  {t("homepage.servicePage.filters.resetFilters", "Reset filters")}
                </button>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="p-3 sm:p-4 border-b bg-blue-50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-blue-600">{totalCount || 0}</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-0.5">{t("homepage.servicePage.stats.availableProfessionals")}</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-blue-600">
                  {professionals && professionals.length > 0
                    ? (professionals.reduce((sum, prof) => sum + (prof.rating || 0), 0) / professionals.length).toFixed(1)
                    : "0.0"
                  }
                </div>
                <div className="text-xs sm:text-sm text-gray-600 mt-0.5">{t("homepage.servicePage.stats.averageRating")}</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-blue-600">30min</div>
                <div className="text-xs sm:text-sm text-gray-600 mt-0.5">{t("homepage.servicePage.stats.avgResponseTime")}</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-blue-600">
                  {professionals && professionals.length > 0
                    ? Math.round((professionals.filter(prof => prof.backgroundChecked || prof.insuranceVerified).length / professionals.length) * 100)
                    : 0
                  }%
                </div>
                <div className="text-xs sm:text-sm text-gray-600 mt-0.5">{t("homepage.servicePage.stats.verifiedProfessionals")}</div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-4 sm:p-5 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
              <AnimatePresence mode="wait">
                {!isLoading && professionals && professionals.length > 0 ? (
                  professionals.map((professional, index) => (
                    <motion.div
                      key={professional.id}
                      initial={{ opacity: 0, y: 30, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ 
                        delay: index * 0.08,
                        duration: 0.4,
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                    >
                      <ServiceCard 
                        professional={professional} 
                        onMessage={handleMessage} 
                        onContact={handleContact} 
                        onViewProfile={(professional) => { 
                          setSelectedProfessional(professional); 
                          setShowProfileDialog(true); 
                        }} 
                      />
                    </motion.div>
                  ))
                ) : isLoading ? (
                  // Loading skeleton - show while loading
                  Array.from({ length: 6 }).map((_, index) => (
                    <motion.div 
                      key={`skeleton-${index}`}
                      initial={{ opacity: 0, y: 20 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }} 
                      className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg h-32 sm:h-40 shadow-sm overflow-hidden relative"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
                      <div className="w-full h-full flex flex-col items-center justify-center p-4">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-300 rounded-full mb-3 animate-pulse"></div>
                        <div className="w-24 h-4 bg-gray-300 rounded mb-2 animate-pulse"></div>
                        <div className="w-16 h-3 bg-gray-300 rounded animate-pulse"></div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="col-span-1 lg:col-span-2 text-center py-10">
                    <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">{t("homepage.servicePage.noProfessionalsFound")}</p>
                    <p className="text-gray-400 text-sm">{t("homepage.servicePage.tryAdjusting")}</p>
                    <button
                      onClick={() => handleFilterChange(1)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
                    >
                      {t("homepage.servicePage.filters.applyFilters")}
                    </button>
                  </div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 sm:mt-8 flex justify-center items-center gap-2 pb-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="px-3 sm:px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={currentPage === 1}
                >
                  {t("homepage.servicePage.pagination.previous")}
                </button>
                <div className="flex items-center gap-1 max-w-full overflow-x-auto">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => handlePageChange(i + 1)}
                      className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg hover:bg-gray-50 text-sm transition-colors ${currentPage === i + 1 ? "bg-blue-50 text-blue-600 font-medium border-blue-200 border" : "text-gray-600 border border-transparent"}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="px-3 sm:px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={!hasNextPage}
                >
                  {t("homepage.servicePage.pagination.next")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Nested Modals */}
        <AnimatePresence>
        {showQuoteModal && quoteProfessional && (
          <RequestQuoteModal
            isOpen={true}
            onClose={() => setShowQuoteModal(false)}
            professional={quoteProfessional}
            isProfessional={false}
            onOfferCreated={(offer: any) => {
              setShowQuoteModal(false);
              window.location.href = `/client/MessageAndVideoCall?professionalId=${quoteProfessional.id}&professionalName=${encodeURIComponent(quoteProfessional.name)}&offerId=${offer.id}`;
              // navigate(`/client/MessageAndVideoCall?professionalId=${quoteProfessional.id}&professionalName=${encodeURIComponent(quoteProfessional.name)}&offerId=${offer.id}`);
            }}
          />
        )}
        
        {showChat && (
          <ChatModal isOpen={!!showChat} onClose={() => setShowChat(null)} service={showChat} onLogin={() => { window.location.href = "/auth/register"; }} />
        )}
        
        {showProfileDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              onClick={() => setShowProfileDialog(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="fixed inset-0 sm:inset-4 md:inset-8 lg:inset-12 z-[70] overflow-hidden flex items-center justify-center p-0 sm:p-4"
            >
              <div className="w-full h-full sm:max-w-5xl sm:max-h-[90vh] bg-white sm:rounded-xl shadow-2xl flex flex-col">
                {/* Dialog Header - Fixed */}
                <div className="flex-shrink-0 flex items-center justify-between p-4 sm:p-5 md:p-6 border-b bg-gradient-to-r from-gray-50 to-white">
                  <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 truncate pr-4">
                    {selectedProfessional?.name}&apos;s Profile
                  </h2>
                  <button
                    onClick={() => setShowProfileDialog(false)}
                    className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
                  </button>
                </div>

                {/* Dialog Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5 md:p-6 bg-gray-50">
                  {selectedProfessional && (
                    <MyProfile
                      isAdminEdit={true}
                      isViewing={true}
                      adminUserId={selectedProfessional.id}
                      onUserUpdated={() => {
                        // Refresh the professionals list if needed
                        // handleSearch();
                      }}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const KokobeoHomepage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split("-")[0];
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(!localStorage.getItem("hasSeenWelcomePopup"));
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [showHiring, setShowHiring] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);
  const [showChat, setShowChat] = useState(null);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const { isAuthenticated, rehydrated, user } = useSelector((state: any) => state.auth);
  const { typeCategories } = useSelector((state: any) => state.client);

  // Get random icon for categories
  const getRandomIcon = () => {
    return randomIcons[Math.floor(Math.random() * randomIcons.length)];
  };

  // Transform API categories to display format
  const transformCategories = (categories) => {
    if (!categories || !Array.isArray(categories)) return [];

    return categories.slice(0, 6).map((category, index) => ({
      ...category,
      icon: getRandomIcon()
    }));
  };

  // Transform API categories to display format
  const transformAllCategories = (categories) => {
    if (!categories || !Array.isArray(categories)) return [];

    return categories.map((category, index) => ({
      ...category,
      icon: getRandomIcon()
    }));
  };

  // Filter categories based on search query (including translated names)
  const filterCategories = (categories, query) => {
    if (!query.trim()) return categories;

    const lowerQuery = query.toLowerCase();
    return categories.filter(category => {
      const englishName = category.name?.toLowerCase() || "";
      const translatedName = getTranslatedCategoryName(category, currentLang)?.toLowerCase() || "";
      return englishName.includes(lowerQuery) || translatedName.includes(lowerQuery);
    });
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Add to search history if query is meaningful
    if (value.trim().length >= 3 && !searchHistory.includes(value.trim())) {
      setSearchHistory(prev => [value.trim(), ...prev.slice(0, 4)]);
    }
  };

  // Handle search history click
  const handleSearchHistoryClick = (query) => {
    setSearchQuery(query);
    setShowSearchSuggestions(false);
  };

  const handleShowLogin = () => {

    setShowLoginPopup(true);
  };
  const handleContinueWithEmail = () => {
    window.location.href = "/auth/register";
    setShowLoginPopup(false);
  };
  const handleLogin = () => {
    window.location.href = "/auth/login";
    setShowLoginPopup(false);
  };

  const dispatch = useAppDispatch();

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        await dispatch(getTypeCategories({
          id: [TypeEnum.Home_Service, TypeEnum.Professional_Service].toString()
        }));
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [dispatch]);

  // Debounced search effect for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length > 1) {
        const transformedCategories = transformAllCategories(typeCategories);
        const filtered = filterCategories(transformedCategories, searchQuery);
        setFilteredCategories(filtered);
      }
      else {
        const transformedCategories = transformCategories(typeCategories);
        const filtered = filterCategories(transformedCategories, searchQuery);
        setFilteredCategories(filtered);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, typeCategories]);

  // Handle click outside to close search suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.getElementById("search-container");
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowSearchSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      const response = await dispatch(LogoutUser({})).unwrap();
    } catch (error: any) {
      if (error === 401) {
        dispatch(LogoutManually());
      } else {
      }
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col">
      <header className="fixed w-full bg-white shadow-sm z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button onClick={() => setShowMobileMenu(true)} className="flex items-center text-gray-700">
                <span className="hidden sm:inline mr-2">{t("homepage.header.menu")}</span>
                <span className="w-5 h-5 sm:w-6 sm:h-6">☰</span>
              </button>
              
              <a href="/" className="flex items-center no-underline">
                <img src="https://assests.netlify.app/assets/images/logo.png" alt="Kokobeo" className="h-6 sm:h-8 w-auto" />
                <span className="ml-2 text-blue-600 text-lg sm:text-xl font-bold">Kokobeo</span>
              </a>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              {/* Language Selector */}
              <LanguageSelector variant="modal" />
              
              {/* Professional Button - Hide text on very small screens */}
              <a 
                href="/professional" 
                className="bg-blue-600 text-white px-2 sm:px-4 md:px-6 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base rounded-md hover:bg-blue-700 transition-colors no-underline whitespace-nowrap"
              >
                <span className="hidden xs:inline">{t("homepage.header.professional")}</span>
                <span className="xs:hidden">{t("homepage.header.professional")}</span>
              </a>
            </div>
          </div>
        </div>
      </header>
      <MobileMenu2
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        currentUser={user} />
      <main className="flex-1 pt-20 sm:pt-24 pb-24 flex flex-col items-center justify-center px-3 sm:px-4">
        <div className="w-full max-w-[800px] mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-900 mb-6 sm:mb-8">{t("homepage.main.title")}</h1>
          <div className="w-full mx-auto mb-8 sm:mb-12 relative" id="search-container">
            <div className="relative">
              <input
                type="text"
                placeholder={isLoading ? t("homepage.main.loadingCategories") : t("homepage.main.searchPlaceholder")}
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowSearchSuggestions(true)}
                disabled={isLoading}
                className="w-full h-12 sm:h-14 pl-10 sm:pl-12 pr-4 rounded-full border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)] placeholder-gray-400 text-gray-700 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>
          <div className="w-full grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {isLoading || !typeCategories || typeCategories.length === 0 ? (
              // Loading skeleton - show while loading OR if no categories from API
              Array.from({ length: 6 }).map((_, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white rounded-lg h-24 sm:h-[120px] shadow-[0_2px_8px_rgba(0,0,0,0.05)] animate-pulse">
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-300 rounded mb-2"></div>
                    <div className="w-16 h-3 bg-gray-300 rounded"></div>
                  </div>
                </motion.div>
              ))
            ) : filteredCategories.length > 0 ? (
              filteredCategories.map((category, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white rounded-lg h-24 sm:h-[120px] shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] transition-all">
                  <button onClick={() => setSelectedService(category.id)} className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50">
                    <category.icon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mb-2" />
                    <p className="text-xs sm:text-sm text-center text-gray-700 px-2 line-clamp-2">{getTranslatedCategoryName(category, currentLang)}</p>
                  </button>
                  {category.id === "emergency" && (
                    <button onClick={() => setShowChat(getTranslatedCategoryName(category, currentLang))} className="absolute bottom-2 right-2 text-xs text-blue-600 hover:text-blue-800"></button>
                  )}
                </motion.div>
              ))
            ) : searchQuery ? (
              <div className="col-span-2 sm:col-span-3 text-center py-8">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">{t("homepage.main.noCategoriesFound", { query: searchQuery })}</p>
                <p className="text-gray-400 text-sm">{t("homepage.main.tryAdjusting")}</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
                >
                  {t("homepage.main.clearSearch")}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </main>
      {!isLoading && <SharedFooter3 />}
      <InfoPopup isOpen={showHowItWorks} onClose={() => setShowHowItWorks(false)} title="How does it work?" content={<div className="space-y-4 text-left"><p>Find the perfect professional for your needs:</p><ul className="list-disc pl-4"><li>Search for professionals in your area</li><li>Compare multiple quotes</li><li>Read verified reviews from other customers</li><li>Choose your preferred professional and get started</li></ul></div>} />
      <InfoPopup isOpen={showHiring} onClose={() => setShowHiring(false)} title="Hire your professional" content={<div className="space-y-4 text-left"><p>Direct hiring process:</p><ul className="list-disc pl-4"><li>Browse professional profiles</li><li>View their experience and certifications</li><li>Contact them directly</li><li>Discuss your project details</li><li> Agree on terms and get started</li></ul></div>} />
      <InfoPopup isOpen={showQuotes} onClose={() => setShowQuotes(false)} title="Get quotes" content={<div className="space-y-4 text-left"><p>Receive up to 5 quotes from different professionals:</p><ul className="list-disc pl-4"><li>Submit your request</li><li>Get multiple quotes</li><li>Compare prices and services</li><li>Choose the best offer</li><li>Provide anonymous feedback to get better offers</li></ul></div>} />
      <InfoPopup isOpen={showReviews} onClose={() => setShowReviews(false)} title="Reviews" content={<div className="space-y-4 text-left"><p>Once you&apos;ve completed work with your chosen professional, let other customers know about your experience:</p><ul className="list-disc pl-4"><li>Rate your experience</li><li>Share detailed feedback</li><li>Help others make informed decisions</li><li>Build trust in our community</li></ul></div>} />
      <WelcomePopup isOpen={showWelcomePopup} onClose={() => setShowWelcomePopup(false)} />
      <AnimatePresence>
        {selectedService && (
          <ServicePage
            service={getTranslatedCategoryName(filteredCategories.find(cat => cat.id === selectedService), currentLang) || "Professional Service"}
            onClose={() => setSelectedService(null)}
            onLogin={handleShowLogin}
            selectedCategoryId={selectedService}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showChat && (
          <ChatModal
            isOpen={!!showChat}
            onClose={() => setShowChat(null)}
            service={showChat}
            onLogin={handleShowLogin}
          />
        )}
      </AnimatePresence>
      <LoginRegistrationPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
        handleContinueWithEmail={handleContinueWithEmail}
        handleLogin={handleLogin}
      />
      <PolicyModals showPrivacyModal={showPrivacyModal} showTermsModal={showTermsModal} onClosePrivacy={() => setShowPrivacyModal(false)} onCloseTerms={() => setShowTermsModal(false)} showProfessionalTermsModal={undefined} onCloseProfessionalTerms={undefined} />
    </div>
  );
};

const Button = ({ className, variant, onClick, children }) => {
  const baseStyle = "px-4 py-2 rounded-lg font-medium";
  const variantStyle = variant === "outline" ? "border border-gray-300 text-gray-700 hover:bg-gray-50" : "bg-blue-600 text-white hover:bg-blue-700";
  return (<button className={`${baseStyle} ${variantStyle} ${className}`} onClick={onClick}>{children}</button>);
};

const EMPTY_LOCATION_FILTERS = {
  provinces: [] as string[],
  citiesByProvince: {} as Record<string, string[]>,
  municipalities: [] as string[]
};

interface SearchableDropdownProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const effectiveDisabled = disabled || options.length === 0;

  const filteredOptions = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    if (!normalizedTerm) {
      return options;
    }
    return options.filter((option) =>
      option.toLowerCase().includes(normalizedTerm)
    );
  }, [options, searchTerm]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="space-y-1" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          disabled={effectiveDisabled}
          onClick={() => {
            if (!effectiveDisabled) {
              setIsOpen((prev) => !prev);
            }
          }}
          className={`w-full flex items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${effectiveDisabled ? "cursor-not-allowed bg-gray-100 text-gray-400" : "hover:border-blue-400"}`}
        >
          <span className={`truncate ${value ? "text-gray-900" : "text-gray-500"}`}>
            {value || placeholder}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
        {value && !effectiveDisabled && (
          <button
            type="button"
            className="absolute inset-y-0 right-8 flex items-center pr-2 text-gray-400 hover:text-gray-600"
            onClick={(event) => {
              event.stopPropagation();
              onChange("");
            }}
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {isOpen && !effectiveDisabled && (
          <div className="absolute left-0 right-0 z-30 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
              <Search className="h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full border-none p-0 text-sm focus:outline-none"
                placeholder="Search..."
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1 text-sm">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`flex w-full items-center justify-between px-3 py-2 text-left hover:bg-blue-50 ${
                      option === value ? "bg-blue-50 text-blue-600" : "text-gray-700"
                    }`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onChange(option);
                      setIsOpen(false);
                    }}
                  >
                    <span>{option}</span>
                    {option === value && <Check className="h-4 w-4" />}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-gray-500">No results found</div>
              )}
            </div>
          </div>
        )}
      </div>
      {effectiveDisabled && (
        <p className="text-xs text-gray-500">No options available.</p>
      )}
    </div>
  );
};

export default KokobeoHomepage;
